import { ComponentInterface, Entity, lerp } from '@goodboydigital/odie';
import { Container, Sprite, Text, utils } from 'pixi.js';

import { Shared } from '../../App';
import { VehicleEntity } from '../entities/VehicleEntity';

export class TagComponent implements ComponentInterface
{
    public vehicle!: VehicleEntity;
    public isOn = false;
    public view: Container;
    public label: Text;
    public flag: Sprite;

    constructor()
    {
        this.view = new Container();

        this.label = new Text(`LABEL`, {
            fontFamily: 'lubalin',
            fill: 0xFFFFFF,
            align: 'center',
            fontSize: 30,
            lineJoin: 'round',
            stroke: 'black',
            strokeThickness: 2,
        });
        this.label.anchor.set(0.5);
        this.label.scale.set(0.75);
        this.view.addChild(this.label);

        this.flag = new Sprite();
        this.flag.anchor.set(0.5);
        this.flag.scale.set(0.5);
        this.view.addChild(this.flag);
    }

    public addedToEntity(entity: Entity): void
    {
        if (!(entity instanceof VehicleEntity)) throw new Error('Tag component only works with a VehicleEntity');
        this.vehicle = entity as VehicleEntity;
        this.vehicle.view.addChild(this.view);

        this.vehicle.physics.physicsFreeUpdateRunner.add(this as any);
        this.vehicle.onCrash.connect(() => this.onCrash());
        this.vehicle.onRespawn.connect(() => this.onRespawn());
        this.isOn = false;
        this.view.renderable = false;
    }

    public reset(): void
    {
        this.turnOff();
    }

    public turnOn(): void
    {
        if (this.isOn) return;
        this.isOn = true;
        this.view.x = this.vehicle.character.physics.x;
        this.view.y = this.vehicle.character.physics.y;
        this.view.renderable = true;
        this.physicsFreeUpdate();
        this.show();
    }

    public turnOff(): void
    {
        if (!this.isOn) return;
        this.isOn = false;
        if (this.vehicle.game) this.vehicle.game.tween.kill(this.view);
        this.view.renderable = false;
        this.view.alpha = 0;
    }

    private show(): void
    {
        if (!this.vehicle.game) return;
        this.view.alpha = 0;
        this.vehicle.game.tween.kill(this.view);
        this.vehicle.game.tween.to(this.view, { alpha: 1, duration: 0.3, ease: 'sine.in' });
    }

    public setNickname(name: string): void
    {
        this.label.text = name.substr(0, 3).toUpperCase();
        this.organise();
    }

    public setFlag(name: string): void
    {
        this.flag.texture = Shared.assets.getTexture(`flag-${name}`);
        this.organise();
    }

    public organise(): void
    {
        this.label.x = -(this.label.width / 2) - 1;
        this.label.y = -1;
        this.flag.x = (this.flag.width / 2) + 1;
    }

    public physicsFreeUpdate(): void
    {
        if (!this.vehicle) return;
        if (this.isOn)
        {
            const a = lerp(this.view.rotation, -this.vehicle.character.physics.angle, 0.05);
            const r = a + Math.PI;
            const x = this.vehicle.character.physics.x + (Math.sin(-r) * 70);
            const y = this.vehicle.character.physics.y + (Math.cos(-r) * 70);

            this.view.x = lerp(this.view.x, x, 0.6);
            this.view.y = lerp(this.view.y, y, 0.6);
            this.view.rotation = a;
            this.view.renderable = this.vehicle.chassis.view.renderable;
        }
        else
        {
            this.view.x = this.vehicle.character.physics.x;
            this.view.y = this.vehicle.character.physics.y;
            this.view.rotation = 0;
            this.view.renderable = false;
        }
    }

    public updateColor(color: string): void
    {
        this.label.tint = utils.string2hex(color);
    }

    private onCrash(): void
    {
        this.turnOff();
    }

    private onRespawn(): void
    {
        this.turnOn();
    }
}
